import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Object Height Estimator — uses motion + camera. User stands at a known
// distance from an object, points the phone at the base, taps to record
// angle, then points at the top and taps again. Height computed by
//   h = d * (tan(top) - tan(base)) + eyeHeight
//
// Where:
//   d         = horizontal distance from observer to object
//   top       = angle above horizontal when phone is pointed at the top
//   base      = angle BELOW horizontal when phone is pointed at the base
//               (negative angle in our convention; tan(base) < 0)
//   eyeHeight = added so the final value is the height of the object's
//               top above the ground (since the formula is from the eye)
//
// Camera is shown for aiming only — we don't capture or process frames.

export default function ObjectHeightEstimator() {
  const [motionState, setMotionState] = useState("idle");
  const [camState, setCamState] = useState("idle");
  const [distance, setDistance] = useState("10");
  const [eyeHeight, setEyeHeight] = useState("5.5");
  const [unit, setUnit] = useState("ft"); // ft | m
  const [baseAngle, setBaseAngle] = useState(null);
  const [topAngle, setTopAngle] = useState(null);
  const [step, setStep] = useState("setup"); // setup | base | top | done
  const [livePitch, setLivePitch] = useState(0);
  const motionHandler = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const enable = async () => {
    // Motion permission
    if (typeof DeviceOrientationEvent === "undefined") { setMotionState("unsupported"); return; }
    try {
      if (typeof DeviceOrientationEvent.requestPermission === "function") {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== "granted") { setMotionState("denied"); return; }
      }
      motionHandler.current = (e) => {
        // When phone is held vertically (screen facing user), beta near 90°
        // means looking at horizon. The pitch we want is (beta - 90), where
        // positive = looking up, negative = looking down.
        const pitch = (e.beta || 0) - 90;
        setLivePitch(pitch);
      };
      window.addEventListener("deviceorientation", motionHandler.current, true);
      setMotionState("ok");
    } catch {
      setMotionState("denied");
      return;
    }

    // Camera permission
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) { setCamState("unsupported"); return; }
    if (!window.isSecureContext) { setCamState("unsupported"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      // V57.6: srcObject attachment moved to post-render useEffect.
      // The <video> element is conditionally rendered (state === "ok")
      // and isn't in the DOM at this point — assigning srcObject here
      // would silently no-op on a null ref.
      setCamState("ok");
      setStep("base");
    } catch (err) {
      // V57.5: differentiate error types so the UI is honest
      if (err && err.name === "NotAllowedError") setCamState("denied");
      else if (err && err.name === "NotFoundError") setCamState("nodevices");
      else setCamState("denied");
    }
  };

  useEffect(() => () => {
    if (motionHandler.current) window.removeEventListener("deviceorientation", motionHandler.current, true);
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
  }, []);

  // V57.6: attach stream after the <video> element renders.
  useEffect(() => {
    if (camState === "ok" && streamRef.current && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [camState]);

  const recordBase = () => { setBaseAngle(livePitch); setStep("top"); };
  const recordTop = () => { setTopAngle(livePitch); setStep("done"); };
  const reset = () => { setBaseAngle(null); setTopAngle(null); setStep("base"); };

  let height = null;
  if (step === "done" && baseAngle !== null && topAngle !== null) {
    const d = parseFloat(distance);
    const eh = parseFloat(eyeHeight) || 0;
    if (!isNaN(d) && d > 0) {
      const tBase = Math.tan(baseAngle * Math.PI / 180);
      const tTop = Math.tan(topAngle * Math.PI / 180);
      height = d * (tTop - tBase) + eh;
    }
  }

  const inputStyle = {
    width: "100%", background: "#13141F", border: "1px solid #262839",
    borderRadius: 10, color: "#E8E9F3", fontSize: 15, padding: "12px 14px",
    boxSizing: "border-box",
  };
  const labelStyle = { fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6, marginTop: 14 };

  // Setup screen
  if (motionState !== "ok" || camState !== "ok") {
    return (
      <ToolsShell title="Object Height">
        <p style={{ fontSize: 14, color: "#C8CADF", marginTop: 0 }}>
          Estimate the height of a tree, building, or anything tall — from a known distance away. Uses your camera (for aiming) and motion sensors (for the angles).
        </p>

        <label style={labelStyle}>Distance to object</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={distance} onChange={(e) => setDistance(e.target.value)} inputMode="decimal" style={{ ...inputStyle, flex: 1 }} />
          <select value={unit} onChange={(e) => setUnit(e.target.value)} style={{ ...inputStyle, width: 80 }}>
            <option value="ft">ft</option>
            <option value="m">m</option>
          </select>
        </div>

        <label style={labelStyle}>Your eye height (above ground)</label>
        <input value={eyeHeight} onChange={(e) => setEyeHeight(e.target.value)} inputMode="decimal" style={inputStyle} />

        <div style={{ marginTop: 22, textAlign: "center" }}>
          <p style={{ fontSize: 13, color: "#C8CADF", marginBottom: 18 }}>
            Tap Start to enable motion sensors and camera. Both stay on your device.
          </p>
          <button onClick={enable} style={primaryBtn}>Start measuring</button>
          {motionState !== "ok" && motionState !== "idle" && <SensorNotice state={motionState} what="Motion sensor" />}
          {camState !== "ok" && camState !== "idle" && <SensorNotice state={camState} what="Camera" />}
        </div>
      </ToolsShell>
    );
  }

  // Measurement steps
  return (
    <ToolsShell title="Object Height">
      <div style={{ position: "relative", width: "100%", maxWidth: 480, aspectRatio: "3 / 4", background: "#000", borderRadius: 12, overflow: "hidden", margin: "0 auto" }}>
        <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
        {/* Crosshair */}
        <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 2, background: TOOLS_ACCENT, opacity: 0.7 }} />
        <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 2, background: TOOLS_ACCENT, opacity: 0.7 }} />
        {/* Pitch readout */}
        <div style={{
          position: "absolute", top: 12, right: 12,
          background: "rgba(0, 0, 0, 0.7)", color: "#fff",
          padding: "6px 12px", borderRadius: 8,
          fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums",
        }}>
          {livePitch.toFixed(1)}°
        </div>
      </div>

      <div style={{ marginTop: 18 }}>
        {step === "base" && (
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: 14, color: "#C8CADF" }}>
              <strong>Step 1:</strong> Aim crosshair at the <strong>base</strong> of the object.
            </p>
            <button onClick={recordBase} style={{ ...primaryBtn, marginTop: 10 }}>Record base angle</button>
          </div>
        )}
        {step === "top" && (
          <div style={{ textAlign: "center" }}>
            <p style={{ fontSize: 14, color: "#C8CADF" }}>
              <strong>Step 2:</strong> Aim crosshair at the <strong>top</strong> of the object.
            </p>
            <div style={{ fontSize: 12, color: "#7C8099", marginTop: 4 }}>Base angle recorded: {baseAngle.toFixed(1)}°</div>
            <button onClick={recordTop} style={{ ...primaryBtn, marginTop: 10 }}>Record top angle</button>
          </div>
        )}
        {step === "done" && (
          <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "20px 22px", marginTop: 4 }}>
            <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 6 }}>Estimated height</div>
            <div style={{ fontSize: 42, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
              {height !== null ? `${height.toFixed(1)} ${unit}` : "—"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 16 }}>
              <Mini label="Base angle" value={`${baseAngle.toFixed(1)}°`} />
              <Mini label="Top angle" value={`${topAngle.toFixed(1)}°`} />
            </div>
            <button onClick={reset} style={{ marginTop: 16, background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13, cursor: "pointer", width: "100%" }}>
              Measure again
            </button>
          </div>
        )}
      </div>

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
        Accuracy depends on knowing the distance correctly. Walk-counted or laser-measured distances work best.
      </p>
    </ToolsShell>
  );
}

function Mini({ label, value }) {
  return (
    <div style={{ background: "#0F1018", borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 11, color: "#7C8099", marginBottom: 2, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "#E8E9F3", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}
